import React from 'react';
import { type GetProps, Form, Select } from 'antd';
import { addressFieldId } from './addressField';

/**
 * A reusable form select component with integrated search functionality.
 *
 * Renders an Ant Design `Select` component inside a `Form.Item`, supporting custom options,
 * validation rules, labels, tooltips, and disabling editing. The select options are filterable
 * by their label property, enabling case-insensitive search.
 *
 * @param props - The properties for configuring the SearchSelect component.
 * @param props.addressFieldId - The unique identifier for the form field.
 * @param props.rules - Validation rules for the form item.
 * @param props.label - The label displayed for the form item.
 * @param props.required - Whether the field is required.
 * @param props.tooltip - Optional tooltip for the form item.
 * @param props.options - The selectable options for the dropdown.
 * @param props.disableEditing - If true, disables editing of the select input.
 * @param props.dataTestId - Optional test id for testing purposes.
 */

export interface SearchSelectProps {
	addressFieldId: addressFieldId;
	required: boolean;
	options?: { value?: string; label?: string }[];
	disableEditing?: boolean;
	dataTestId?: string;
	rules?: GetProps<typeof Form.Item>['rules'];
	label?: React.ReactNode;
	tooltip?: React.ReactNode;
}

export const SearchSelect = (props: SearchSelectProps) => {
	return (
		<Form.Item
			name={props.addressFieldId}
			htmlFor={props.dataTestId || props.addressFieldId}
			rules={props.rules}
			label={props.label}
			required={props.required}
			tooltip={props.tooltip}
		>
			<Select
				id={props.dataTestId || props.addressFieldId}
				showSearch
				optionFilterProp="label"
				filterOption={(inputValue, option) =>
					option?.label?.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
				}
				options={props.options}
				disabled={props.disableEditing}
				data-testid={props.dataTestId}
			/>
		</Form.Item>
	);
};
